/**
 * #1149: exact arithmetic for Novig's $0.01 native contracts.
 *
 * Tests are the only initial consumers. Arithmetic acceptance is not account,
 * market, balance, fee, risk or execution acceptance. No terms are snapped.
 * Contract: docs/261012NovigSharedVenueContract.md.
 */
export interface NovigNativeOrderAmounts {
  priceProbability: string;
  quantityNativeContracts: number;
  purchaseCostUsd: string;
  winningPayoutUsd: string;
}

export type NovigNativeAmountResult =
  | { ok: true; amounts: NovigNativeOrderAmounts }
  | { ok: false; reason: 'invalid-probability' | 'off-grid-probability' | 'invalid-native-quantity' };

/** Convert canonical probability text to an exact integer, never USD. */
function probabilityThousandths(price: unknown): number | null {
  if (typeof price !== 'string' || price.length !== 5 || !/^0\.\d{3}$/.test(price)) return null;
  const value = Number(price.slice(2));
  return value >= 1 && value <= 999 ? value : null;
}

function onGrid(value: number): boolean {
  return value <= 50 || value >= 950 || (value >= 55 && value <= 945 && value % 5 === 0);
}

/** True only for canonical text on the venue's 279-price grid. */
export function isNovigProbabilityOnGrid(price: unknown): boolean {
  const value = probabilityThousandths(price);
  return value !== null && onGrid(value);
}

/** Outcome complement only; this is not a Close/sell execution translation. */
export function novigComplementProbability(price: unknown): string | null {
  const value = probabilityThousandths(price);
  if (value === null || !onGrid(value)) return null;
  return `0.${String(1000 - value).padStart(3, '0')}`;
}

function formatUsdUnits(units: bigint): string {
  const scale = 100_000n;
  return `${units / scale}.${String(units % scale).padStart(5, '0')}`;
}

/**
 * Gross order cost in $0.00001 units = probability-thousandths × native quantity.
 * Charged fees are separate evidence; this module does not infer or include them.
 * Payout in those units = native quantity × 1000. BigInt protects the products
 * even when the JSON-safe quantity itself is near Number.MAX_SAFE_INTEGER.
 * That serialization bound is not a discovered venue/account quantity limit.
 */
export function computeNovigNativeOrderAmounts(
  priceProbability: unknown,
  quantityNativeContracts: unknown,
): NovigNativeAmountResult {
  const price = probabilityThousandths(priceProbability);
  if (price === null) return { ok: false, reason: 'invalid-probability' };
  if (!onGrid(price)) return { ok: false, reason: 'off-grid-probability' };
  if (typeof quantityNativeContracts !== 'number'
    || !Number.isSafeInteger(quantityNativeContracts) || quantityNativeContracts <= 0) {
    return { ok: false, reason: 'invalid-native-quantity' };
  }
  const quantity = BigInt(quantityNativeContracts);
  return {
    ok: true,
    amounts: {
      priceProbability: priceProbability as string,
      quantityNativeContracts,
      purchaseCostUsd: formatUsdUnits(BigInt(price) * quantity),
      winningPayoutUsd: formatUsdUnits(1000n * quantity),
    },
  };
}
